<script>
import { mapGetters } from 'vuex'
import ChargeResource from '@/resources/charge'
import PaymentMethod from '../components/PaymentMethod.vue'
import CostBreakdown from '../components/CostBreakdown.vue'
import UserEditForm from '../components/UserEditForm.vue'
import VoucherPitch from '../components/VoucherPitch.vue'
import TermsOfServiceLink from '../components/TermsOfServiceLink.vue'
import WaiverModal from '../components/WaiverModal.vue'
import UserUpcomingReservations from '../components/UserUpcomingReservations.vue'
import ReservationRacersForm from '../components/ReservationRacersForm.vue'
import ReservationSeatingGroupResource from '@/resources/reservationSeatingGroup'
import messages from '@/mixins/messages'

export default {
    name: 'Dashboard',
    mixins: [messages],
    components: {
        PaymentMethod,
        CostBreakdown,
        UserEditForm,
        VoucherPitch,
        TermsOfServiceLink,
        WaiverModal,
        UserUpcomingReservations,
        ReservationRacersForm,
    },
    computed: {
        ...mapGetters(['userResource', 'isApexClub', 'creditsBalance']),
        waiversOutstandingIds() {
            return this.$refs.waiverModal.waiversOutstanding.map((waiver) => waiver.id)
        },
        waiverApprovalsIds() {
            return this.$refs.waiverModal.waiverApprovals.map((approval) => approval.id)
        },
    },
    data() {
        return {
            loading: true,
            showPaymentMethod: true,
            chargeResources: [],
            currentTabIndex: 0,
            lapsPage: null,
            lapsNoneReorded: false,
            lapTimeQuery: '',
            lapsLoading: false,
            calendarResource: null,
            waiverResources: [],
            waiverViewing: null,
            optInChangesMade: false,
            optInAccount: null,
            optInMarketing: null,
            upcomingReservationsData: null,
            myRacerGroups: [],
            editingGroup: null,
            savingRacers: false,
        }
    },
    methods: {
        async load() {
            this.loading = true
            this.chargeResources = await ChargeResource.all({ query: { amount_cents__gt: 0 } })
            ;(this.optInAccount = this.userResource.attributes.opt_in_account_updates), (this.optInMarketing = this.userResource.attributes.opt_in_marketing_updates)
            this.loading = false

            this.$refs.waiverModal.$on('waivers', (waivers) => (this.waiverResources = waivers))
            this.loadRacerGroups()
        },
        async loadRacerGroups() {
            try {
                const groups = await ReservationSeatingGroupResource.mine()
                this.myRacerGroups = groups.filter((group) => String(group.attributes.created_by) === String(this.userResource.id))
            } catch (e) {
                this.myRacerGroups = []
            }
        },
        groupReservationIds(group) {
            return group.seats().map((seat) => seat.reservation)
        },
        openEditRacers(group) {
            this.editingGroup = group
            this.$refs.editRacersModal.show()
        },
        async saveEditRacers() {
            this.savingRacers = true
            const ok = this.$refs.editRacersForm ? await this.$refs.editRacersForm.run() : true
            this.savingRacers = false
            if (ok) {
                this.$refs.editRacersModal.hide()
                this.editingGroup = null
                await this.loadRacerGroups()
            }
        },
        async onUnenrollSubscription() {
            this.$refs.cancelSubscription.hide()
            await ChargeResource.creditUnenrollUser()
            this.userResource.set('credits_subscription_in_good_standing', false)
        },
        async onSaveNotifPrefs() {
            this.userResource.attributes.opt_in_account_updates = this.optInAccount
            this.userResource.attributes.opt_in_marketing_updates = this.optInMarketing
            await this.userResource.save()
            this.optInChangesMade = false
        },
        nthDayOfMonth(day) {
            const moment = require('moment')
            return moment(day, 'D').format('Do')
        },
    },
    async mounted() {
        await this.load()
    },
}
</script>

<template>
    <div>
        <header-image src="hero-main.png">
            <h1>Übersicht</h1>
        </header-image>

        <b-container class="mt-3 mb-5" v-if="!loading">
            <b-row>
                <b-col>
                    <user-upcoming-reservations poll-seconds="5" @reservations="(reservationData) => (upcomingReservationsData = reservationData)" />

                    <div v-if="$appConfigBool('APEX_CLUB_ENABLED', true) && !isApexClub">
                        <b-alert variant="info" class="mb-0" show
                            ><strong>Spare 20 % bei jedem Rennen</strong>, indem du <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong
                            > beitrittst. (Und erhalte noch vieles mehr!) <a href="javascript:{}" @click="$router.push({ name: 'subscription' })">Weitere Informationen</a></b-alert
                        >
                    </div>

                    <b-card class="mb-3 mt-3" no-body>
                        <b-tabs class="scrollable-tabs-header" card>
                            <b-tab title="Profil">
                                <b-row>
                                    <b-col offset-md="2" md="8">
                                        <user-edit-form :user-resource="userResource" submit-text="Profil speichern" success-message="Profil gespeichert!" />
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Zahlung">
                                <b-row>
                                    <b-col offset-md="3" md="6">
                                        <b-card>
                                            <payment-method cols-lg="12" :userResource="userResource" />
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Mitgliedschaft" v-if="$appConfigBool('APEX_CLUB_ENABLED', true) && isApexClub">
                                <dl v-if="userResource.attributes.credits_subscription_in_good_standing" class="row mb-0">
                                    <dt class="col-sm-4">Apex Club</dt>
                                    <dd class="col-sm-8">
                                        <strong class="text-success"><b-icon icon="patch-check-fill" /> Mitglied</strong>
                                    </dd>
                                    <dt class="col-sm-4">Guthaben</dt>
                                    <dd class="col-sm-8">
                                        <strong><b-icon icon="currency-exchange" /> {{ creditsBalance }} Credits</strong>
                                    </dd>
                                    <dt class="col-sm-4">Mitgliedschaft</dt>
                                    <dd class="col-sm-8">
                                        <strong
                                            ><b-icon icon="arrow-repeat" /> {{ userResource.attributes.credits_recurring }} Credits monatlich am {{ nthDayOfMonth(userResource.attributes.credits_cycle_day_of_month) }} — {{ currencySymbol()
                                            }}{{ moneyFormat(userResource.attributes.credits_recurring_cents) }}</strong
                                        >
                                    </dd>
                                    <dt class="col-sm-4">&nbsp;</dt>
                                    <dd class="col-sm-8">
                                        <small><a href="javascript:{};" class="text-muted" @click="$refs.cancelSubscription.show()">Mitgliedschaft kündigen</a></small>
                                    </dd>
                                </dl>

                                <div v-else-if="!userResource.attributes.credits_subscription_in_good_standing && userResource.attributes.credits_subscription_exists">
                                    <b-alert variant="danger" class="mb-3" show
                                        ><strong>Oh nein!</strong> Du hast eine aktive Mitgliedschaft bei {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}, aber dein Konto ist nicht ordnungsgemäß. Bitte <a href="javascript:{}" @click="currentTabIndex = 1">überprüfe deine Zahlungsmethode</a>, um dies zu beheben.</b-alert
                                    >
                                </div>

                                <div v-else>
                                    <b-alert variant="info" class="mb-0" show
                                        ><strong>Spare 20 % bei jedem Rennen</strong>, indem du <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong
                                        > beitrittst. (Und erhalte noch vieles mehr!) <a href="javascript:{}" @click="$router.push({ name: 'subscription' })">Weitere Informationen</a></b-alert
                                    >
                                </div>
                            </b-tab>

                            <b-tab title="Benachrichtigungen">
                                <b-row>
                                    <b-col offset-md="2" md="8">
                                        <b-card>
                                            <small>
                                                <b-form-checkbox v-model="optInAccount" @input="optInChangesMade = true" switch>
                                                    Durch Aktivieren dieses Kontrollkästchens stimme ich dem Erhalt transaktionsbezogener Nachrichten zu meinem Konto, meinen Bestellungen oder von mir angeforderten Dienstleistungen zu. Diese Nachrichten können unter anderem Terminerinnerungen, Bestellbestätigungen und Kontobenachrichtigungen enthalten. Die
                                                    Häufigkeit der Nachrichten kann variieren. Es können Nachrichten- und Datengebühren anfallen. Antworte mit HELP, um Hilfe zu erhalten, oder mit STOP, um dich abzumelden.
                                                </b-form-checkbox>
                                                <b-form-checkbox v-model="optInMarketing" @input="optInChangesMade = true" class="mt-3" switch>
                                                    Durch Aktivieren dieses Kontrollkästchens stimme ich dem Erhalt von Marketing- und Werbenachrichten zu, darunter Sonderangebote, Rabatte und Informationen zu neuen Produkten. Die Häufigkeit der Nachrichten kann variieren. Es können Nachrichten- und Datengebühren anfallen. Antworte mit HELP, um Hilfe zu erhalten, oder mit STOP, um dich
                                                    abzumelden.
                                                </b-form-checkbox>
                                            </small>
                                            <b-button variant="primary" :disabled="!optInChangesMade" @click="onSaveNotifPrefs()" class="mt-3">Benachrichtigungen speichern</b-button>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Bevorstehende Reservierungen">
                                <div v-if="upcomingReservationsData && upcomingReservationsData.future.length > 0">
                                    <b-table-simple class="nowrap-table" responsive>
                                        <b-thead>
                                            <b-th>Datum</b-th>
                                            <b-th>Simulator</b-th>
                                            <b-th>&nbsp;</b-th>
                                            <b-th>&nbsp;</b-th>
                                        </b-thead>
                                        <b-tbody>
                                            <b-tr v-for="(reserv, i) in upcomingReservationsData.future" :key="i">
                                                <b-td>{{ [reserv.getDateFormatted(), reserv.getTimeFormatted()].join(' @ ') }}</b-td>
                                                <b-td>{{ reserv.attributes.calendar_name }}</b-td>
                                                <b-td
                                                    ><a :href="reserv.getAddToCalendarLink()"><b-icon icon="calendar-plus" /> Zum Kalender hinzufügen</a></b-td
                                                >
                                                <b-td
                                                    ><b-link :to="{ name: 'calendarControlWaitingRoom', params: { calendarId: reserv.attributes.calendar } }"><b-icon icon="controller" /> Zur Simulatorsteuerung</b-link></b-td
                                                >
                                            </b-tr>
                                        </b-tbody>
                                    </b-table-simple>
                                </div>

                                <div v-else class="text-center pt-5 pb-5">
                                    <h3><b-icon icon="cone-striped" style="color: #ed8d34" /></h3>
                                    <p>Du hast keine bevorstehenden Reservierungen</p>
                                    <b-button variant="secondary" :to="{ name: 'reservations' }">Verfügbarkeit anzeigen</b-button>
                                </div>
                            </b-tab>

                            <b-tab title="Deine Fahrer" v-if="myRacerGroups.length > 0">
                                <div v-for="group in myRacerGroups" :key="group.id" class="mb-4">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <strong>{{ group.attributes.created ? new Date(group.attributes.created).toLocaleDateString() : 'Buchung' }} &mdash; {{ group.seats().length }} Simulatoren</strong>
                                        <b-button size="sm" variant="secondary" @click="openEditRacers(group)">Fahrer hinzufügen/bearbeiten</b-button>
                                    </div>
                                    <b-list-group>
                                        <b-list-group-item v-for="seat in group.seats()" :key="seat.reservation" class="d-flex justify-content-between">
                                            <span
                                                >{{ seat.calendar_name }} <span class="text-muted small">{{ seat.time ? new Date(seat.time).toLocaleString() : '' }}</span></span
                                            >
                                            <span>{{ seat.seated_user_display_name || 'Frei' }}</span>
                                        </b-list-group-item>
                                    </b-list-group>
                                </div>
                            </b-tab>

                            <b-tab title="Haftungsverzichtserklärungen">
                                <b-table-simple class="nowrap-table" responsive v-if="waiverResources.length > 0">
                                    <b-thead>
                                        <b-th>&nbsp;</b-th>
                                        <b-th>Status</b-th>
                                        <b-th>Name der Erklärung</b-th>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="waiver in waiverResources" :key="waiver.id">
                                            <b-td class="text-right">
                                                <b-button variant="danger" size="sm" v-if="waiversOutstandingIds.includes(waiver.id)" @click="waiverViewing = waiver">Unterschreiben</b-button>
                                                <b-button variant="outline-primary" size="sm" v-else @click="waiverViewing = waiver">Anzeigen</b-button>
                                            </b-td>
                                            <b-td>{{ waiver.attributes.required ? 'Erforderlich' : 'Optional' }}</b-td>
                                            <b-td :class="{ 'font-weight-bold': waiver.attributes.required }">{{ waiver }}</b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>

                                <p v-else class="text-center">Keine Erklärungen zu unterschreiben.</p>
                            </b-tab>

                            <b-tab title="Rundenzeiten" class="text-center">
                                <b-button-group>
                                    <b-button variant="outline-primary" :to="{ name: 'laps' }"><b-icon icon="stopwatch-fill" /> Zu meinen Runden</b-button>
                                    <b-button variant="outline-primary" :to="{ name: 'leaderboards' }"><b-icon icon="table" /> Zu den Bestenlisten</b-button>
                                </b-button-group>
                            </b-tab>

                            <b-tab title="Rennvoreinstellungen" class="text-center">
                                <p>Mach dich auf deine Weise rennbereit. <strong>Passe dein Fahrzeug, die Strecke, Fahrhilfen und Sitzungseinstellungen an</strong>, bevor du in die Startaufstellung fährst. Ob entspannte Ausfahrt oder anspruchsvoller Wettkampf – optimiere dein Erlebnis jederzeit vor Rennbeginn.</p>
                                <b-button variant="outline-primary" :to="{ name: 'racesPresets' }"><b-icon icon="collection-play" /> Dein Rennen anpassen</b-button>
                            </b-tab>

                            <b-tab title="Letzte Transaktionen">
                                <b-table-simple v-if="chargeResources.length > 0" class="nowrap-table mt-2" responsive>
                                    <b-thead>
                                        <b-th>Betrag</b-th>
                                        <b-th>Beschreibung</b-th>
                                        <b-th>Beleg</b-th>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="(chargeResource, i) in chargeResources" :key="i">
                                            <b-td>{{ currencySymbol() }}{{ chargeResource.amount() }}</b-td>
                                            <b-td
                                                >{{ chargeResource.get('notes') || 'Keine Beschreibung' }}<br /><small class="text-muted"
                                                    >{{ chargeResource.createdTimePretty() }} <span class="text-danger" v-if="chargeResource.get('errors')"><br />{{ chargeResource.get('errors') }}</span></small
                                                ></b-td
                                            >
                                            <b-td>
                                                <a :href="chargeResource.get('receipt_url')" target="_blank" v-if="chargeResource.attributes.receipt_url">Anzeigen</a>
                                                <span v-else class="text-muted">&nbsp;</span>
                                            </b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>
                                <p v-else class="text-center">Keine aktuellen Transaktionen</p>
                            </b-tab>
                        </b-tabs>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <b-modal
            title="Mitgliedschaft kündigen"
            ref="cancelSubscription"
            @ok="onUnenrollSubscription()"
            v-if="userResource"
            :ok-disabled="!userResource.attributes.credits_subscription_cancelable"
            cancel-title="Nicht kündigen"
            cancel-variant="primary"
            ok-title="Kündigen"
            ok-variant="danger"
            centered
            no-fade
        >
            <p>
                Möchtest du deine Mitgliedschaft wirklich kündigen? Diese Aktion kann nicht rückgängig gemacht werden.
                <span v-if="creditsBalance > 0"
                    ><strong>Du verlierst dein Guthaben von {{ creditsBalance }} Credits!</strong> Erwäge, deine Credits vor der Kündigung für eine Reservierung zu verwenden.</span
                >
            </p>
            <b-alert variant="danger" :show="!userResource.attributes.credits_subscription_cancelable">
                <strong>Entschuldigung! Du kannst deine Mitgliedschaft noch nicht kündigen</strong>, da du mindestens zwei Abrechnungszeiträume lang Mitglied bleiben musst. Bitte lies unsere <terms-of-service-link text="Nutzungsbedingungen" el="span" />.
            </b-alert>
        </b-modal>

        <waiver-modal ref="waiverModal" v-model="waiverViewing" />

        <b-modal ref="editRacersModal" title="Deine Fahrer" no-fade no-enforce-focus centered @ok.prevent="saveEditRacers" ok-title="Speichern" cancel-title="Schließen" :ok-disabled="savingRacers">
            <reservation-racers-form v-if="editingGroup" ref="editRacersForm" :reservation-ids="groupReservationIds(editingGroup)" :purchaser="userResource" :existing-group="editingGroup" />
        </b-modal>
    </div>
</template>

<style lang="scss">
.add-card {
    padding-top: 10px;
}

.b-icon.gold {
    color: #ecbd00;
}

.lap-times-table {
    overflow: scroll;

    table {
        min-width: 900px;
    }
}

.tabs.scrollable-tabs-header {
    .card-header {
        overflow-x: scroll;
        overflow-y: hidden;
        height: 55px;

        ul.card-header-tabs {
            width: 1200px;
        }
    }
}

.nowrap-table {
    td,
    th {
        white-space: nowrap;
    }
}
</style>
